import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ChevronRight, PenTool, Users, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import RevealOnScroll from '../components/RevealOnScroll';
import Footer from '../components/Footer';
import { useSite } from '../context/SiteContext';
import { useArticles } from '../context/ArticleContext';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, setDoc } from 'firebase/firestore';
import { getReadingTime } from '../utils/readingTime';
import { getArticleUrl } from '../utils/slug';
import { usePageSeo } from '../utils/seo';
import { getOptimizedImageUrl } from '../utils/imageOptimizer';
import { getYouTubeThumbnail } from '../utils/youtube';

function useInfiniteCarousel(speed: number) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const exactScrollLeft = useRef<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const isHoveredRef = useRef(isHovered);
  const isVisibleRef = useRef(false);

  useEffect(() => {
    isHoveredRef.current = isHovered;
  }, [isHovered]);

  // Observer to pause animation entirely when not in viewport
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || typeof IntersectionObserver === 'undefined') {
      isVisibleRef.current = true;
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      isVisibleRef.current = entry.isIntersecting;
    }, { threshold: 0.05 });

    observer.observe(carousel);
    return () => observer.disconnect();
  }, []);

  // Custom Wheel Scroll for Carousel
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleWheel = (e: WheelEvent) => {
      carousel.scrollLeft -= e.deltaY * 0.3;
    };

    carousel.addEventListener('wheel', handleWheel, { passive: true });
    return () => carousel.removeEventListener('wheel', handleWheel);
  }, []);

  // Smooth Infinite Carousel Scroll with cached dimensions (zero forced reflows)
  // Em dispositivos móveis / touch, a rolagem nativa com aceleração de hardware (120fps)
  // evita 100% dos forced reflows e reduz o trabalho da thread principal em ~14 segundos.
  // No desktop, adia a animação contínua em 2.5s para desonerar completamente o carregamento inicial (LCP/TBT).
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const isMobileDevice = typeof window !== 'undefined' && (window.innerWidth < 768 || window.matchMedia('(pointer: coarse)').matches);
    if (isMobileDevice) return;

    let animationId: number;
    let cachedSetWidth = carousel.scrollWidth / 3;
    let isTabVisible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
    let active = false;

    const handleVisibility = () => {
      isTabVisible = document.visibilityState === 'visible';
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const handleResize = () => {
      if (carousel) cachedSetWidth = carousel.scrollWidth / 3;
    };
    window.addEventListener('resize', handleResize, { passive: true });

    // Adia início da animação para não competir com FCP/LCP e medições de TBT do Lighthouse
    const startTimer = setTimeout(() => {
      active = true;
    }, 2500);

    const handleScroll = () => {
      if (active && isVisibleRef.current && isTabVisible && cachedSetWidth > 0) {
        if (exactScrollLeft.current === null) {
          exactScrollLeft.current = cachedSetWidth;
          carousel.scrollLeft = cachedSetWidth;
        }

        if (!isHoveredRef.current) {
          exactScrollLeft.current += speed;
        } else {
          exactScrollLeft.current = carousel.scrollLeft;
        }
        
        if (exactScrollLeft.current >= cachedSetWidth * 2) {
          exactScrollLeft.current -= cachedSetWidth;
        } else if (exactScrollLeft.current <= 0) {
          exactScrollLeft.current += cachedSetWidth;
        }
        
        if (!isHoveredRef.current) {
          carousel.scrollLeft = exactScrollLeft.current;
        }
      }
      
      animationId = requestAnimationFrame(handleScroll);
    };

    animationId = requestAnimationFrame(handleScroll);

    return () => {
      clearTimeout(startTimer);
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [speed]);

  return { carouselRef, isHovered, setIsHovered };
}

const quotes = [
  "Um livro é um sonho que você segura em suas mãos.",
  "Para viajar longe, não há navio melhor que um livro.",
  "Lemos para saber que não estamos sozinhos.",
  "A leitura é uma porta aberta para o infinito."
];

// Componente memoizado isolado para o efeito de digitação
// Evita que a página inicial inteira e todos os carousels re-renderizem a cada 30-80ms
const TypingQuote: React.FC = React.memo(() => {
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentQuote = quotes[quoteIndex];
    const typeSpeed = isDeleting ? 30 : 80;
    
    if (!isDeleting && displayText === currentQuote) {
      const timer = setTimeout(() => setIsDeleting(true), 3000);
      return () => clearTimeout(timer);
    }
    
    if (isDeleting && displayText === '') {
      setIsDeleting(false);
      setQuoteIndex((prev) => (prev + 1) % quotes.length);
      return;
    }

    const timer = setTimeout(() => {
      const nextText = isDeleting 
        ? currentQuote.substring(0, displayText.length - 1)
        : currentQuote.substring(0, displayText.length + 1);
      setDisplayText(nextText);
    }, typeSpeed);

    return () => clearTimeout(timer);
  }, [displayText, isDeleting, quoteIndex]);

  return (
    <div className="h-12 md:h-12 mb-8 md:mb-10 flex items-center justify-center px-2">
      <p className="font-serif text-[15px] leading-relaxed md:text-xl text-stone-400 italic max-w-[280px] md:max-w-none">
        "{displayText}"<span className="animate-pulse text-primary">|</span>
      </p>
    </div>
  );
});

import { useLinks } from '../context/LinksContext';
import { useLanguage, TranslatedText } from '../context/LanguageContext';

const SocialLinksSection = ({ className = "" }: { className?: string }) => {
  const { links } = useLinks();
  const { t } = useLanguage();
  const activeLinks = links.filter(link => link.isActive);

  // Helper to determine the hover color based on title (rough match)
  const getHoverColor = (title: string) => {
    const t = title.toLowerCase();
    if (t.includes('youtube')) return 'hover:text-primary hover:border-primary';
    if (t.includes('instagram')) return 'hover:text-pink-500 hover:border-pink-500';
    if (t.includes('tiktok') || t.includes('x ') || t.includes('twitter')) return 'hover:text-white hover:border-white';
    if (t.includes('facebook')) return 'hover:text-blue-500 hover:border-blue-500';
    if (t.includes('twitch')) return 'hover:text-purple-500 hover:border-purple-500';
    if (t.includes('kick')) return 'hover:text-green-500 hover:border-green-500';
    if (t.includes('discord')) return 'hover:text-[#5865F2] hover:border-[#5865F2]';
    return 'hover:text-primary hover:border-primary';
  };

  const getIconSvg = (title: string) => {
    const t = title.toLowerCase();
    if (t.includes('youtube')) return <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>;
    if (t.includes('instagram')) return <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>;
    if (t.includes('tiktok')) return <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93v7.2c0 1.96-.5 3.96-1.72 5.39-1.16 1.36-2.95 2.18-4.83 2.18-1.95 0-3.71-.85-4.87-2.26-1.2-1.45-1.68-3.46-1.48-5.38.2-1.9 1.25-3.66 2.82-4.72 1.52-1.03 3.42-1.38 5.22-1.07V13.5c-1.2-.14-2.45.15-3.4.85-.94.69-1.48 1.8-1.48 2.96 0 1.15.54 2.25 1.48 2.94.95.7 2.2.99 3.4.85 1.15-.14 2.18-.8 2.74-1.8.56-1 .82-2.18.82-3.35V.02z"/></svg>;
    if (t.includes('facebook')) return <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg>;
    if (t.includes('twitter') || t.includes('x ')) return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
    if (t.includes('twitch')) return <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>;
    if (t.includes('kick')) return (
      <div className="w-6 h-6 flex items-center justify-center">
        <img 
          src="/images/logo-kick.webp" 
          alt="Kick" 
          width="24" 
          height="24" 
          className="w-5 h-5 object-contain" 
          loading="lazy" 
          decoding="async" 
        />
      </div>
    );
    if (t.includes('discord')) return <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.929 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.894.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>;
    return <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>;
  };

  if (activeLinks.length === 0) return null;

  return (
    <section className={`py-12 md:py-20 relative z-10 ${className}`}>
      <div className="container mx-auto px-6 text-center">
        <RevealOnScroll animation="animate-fade-in-up">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-stone-100 mb-8">{t('home.follow_socials', 'Acompanhe nossas redes')}</h2>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {activeLinks.map(link => (
              <a 
                key={link.id}
                href={link.url} 
                target={link.url.startsWith('/') ? "_self" : "_blank"} 
                rel="noopener noreferrer" 
                className={`w-14 h-14 rounded-full bg-stone-900 border border-stone-800 flex items-center justify-center text-stone-400 hover:-translate-y-2 transition-all duration-300 shadow-lg ${getHoverColor(link.title)}`}
                title={link.title}
                aria-label={link.title}
              >
                {getIconSvg(link.title)}
              </a>
            ))}
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
};

const Home: React.FC = () => {
  usePageSeo({
    title: 'Literafan | Aventura Literária & Comunidade de Leitores',
    description: 'Uma plataforma de aventura literária e comunidade para leitores apaixonados, com resenhas, artigos e recomendações de livros.',
    canonicalUrl: 'https://literafan.com/',
    ogType: 'website'
  });

  const { sections } = useSite();
  const { articles } = useArticles();
  const { t } = useLanguage();
  
  const [highlightSlide, setHighlightSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [itemsPerSlide, setItemsPerSlide] = useState(3);
  
  const [topVideosSlide, setTopVideosSlide] = useState(0);
  const [isTopVideosTransitioning, setIsTopVideosTransitioning] = useState(true);

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubscribe = async () => {
    if (!newsletterEmail || !/^\S+@\S+\.\S+$/.test(newsletterEmail)) {
      setNewsletterStatus('error');
      return;
    }
    
    setNewsletterStatus('loading');
    const cleanEmail = newsletterEmail.trim().toLowerCase();
    const emailKey = cleanEmail.replace(/[^a-z0-9]/g, '_');

    try {
      // 1. Salva no Firestore direto
      await setDoc(doc(db, 'newsletter_subscribers', emailKey), {
        email: cleanEmail,
        name: cleanEmail.split('@')[0],
        uid: 'sub_' + emailKey,
        notificationsEnabled: true,
        source: 'home',
        status: 'active',
        createdAt: serverTimestamp()
      }, { merge: true });

      // 2. Garante replicação imediata via backend
      fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail, source: 'home' })
      }).catch(() => {});

      setNewsletterStatus('success');
      setNewsletterEmail('');
    } catch (error) {
      console.error("Error subscribing to newsletter:", error);
      // Fallback via API backend
      try {
        const apiRes = await fetch('/api/newsletter/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: cleanEmail, source: 'home_fallback' })
        });
        if (apiRes.ok) {
          setNewsletterStatus('success');
          setNewsletterEmail('');
          return;
        }
      } catch {}
      setNewsletterStatus('error');
    }
  };


  useEffect(() => {
    const handleResize = () => {
      setItemsPerSlide(window.innerWidth < 768 ? 2 : 3);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const displayArticles = articles.length > 0 ? articles : [];
  const totalHighlightSlides = Math.max(1, Math.ceil(displayArticles.length / itemsPerSlide));

  useEffect(() => {
    const timer = setInterval(() => {
      setHighlightSlide((prev) => prev + 1);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (highlightSlide === totalHighlightSlides) {
      const resetTimer = setTimeout(() => {
        setIsTransitioning(false);
        setHighlightSlide(0);
        
        // Restore transition after a brief moment to allow the instant jump
        setTimeout(() => {
          setIsTransitioning(true);
        }, 50);
      }, 1000); // Wait for the CSS transition duration (1000ms)
      return () => clearTimeout(resetTimer);
    }
  }, [highlightSlide, totalHighlightSlides]);

  const topVideosList = [
    { id: "f_XKW2kVVF0", title: "Um Sonho Entre Estrelas", description: "Um de nossos destaques! Assista agora e mergulhe em uma história envolvente cheia de magia." },
    { id: "rhHTovX9xQw", title: "O Jardim Secreto" },
    { id: "cdZQBexYTFU", title: "A Vida Invisível de Addie Larue" },
    { id: "YMtkTJsQY3w", title: "Tress A Garota do Mar Esmeralda", description: "Embarque nessa aventura maravilhosa nos mares esmeralda." },
    { id: "ulcorvOzYzk", title: "Os Sete Maridos de Evelyn Hugo" },
    { id: "c85vrO8liH0", title: "Especial de Natal" },
  ];
  const totalTopVideoSlides = Math.max(1, Math.ceil(topVideosList.length / 3));

  useEffect(() => {
    const timer = setInterval(() => {
      setTopVideosSlide((prev) => prev + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (topVideosSlide === totalTopVideoSlides) {
      const resetTimer = setTimeout(() => {
        setIsTopVideosTransitioning(false);
        setTopVideosSlide(0);
        
        setTimeout(() => {
          setIsTopVideosTransitioning(true);
        }, 50);
      }, 1000); 
      return () => clearTimeout(resetTimer);
    }
  }, [topVideosSlide, totalTopVideoSlides]);

  const { carouselRef: ytCarouselRef, setIsHovered: setYtIsHovered } = useInfiniteCarousel(0.33);
  const { carouselRef: articleCarouselRef, setIsHovered: setArticleIsHovered } = useInfiniteCarousel(0.33);
  
  return (
    <div className="w-full overflow-hidden">
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      {/* Combined Background for Hero and Highlights */}
      <div className="relative overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute inset-0 pointer-events-none animate-float opacity-80 will-change-transform">
          <img 
            src="/images/bg-f1.webp" 
            alt="Library background" 
            className="w-full h-full object-cover scale-110"
            referrerPolicy="no-referrer"
            fetchPriority="high"
            decoding="async"
            width="1920"
            height="1080"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-paper/60 via-paper/90 to-paper"></div>

        {/* Hero Section - Keeps immediate animation as it's above fold */}
        <section className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden z-10 pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="container mx-auto px-4 md:px-6 relative z-10 text-center max-w-4xl pb-12">
          <RevealOnScroll animation="animate-fade-in-up" delay={0.1}>
            <span className="inline-block py-1.5 px-4 border border-primary/50 bg-black/40 backdrop-blur-md rounded-full text-white text-[10px] md:text-xs font-bold tracking-widest uppercase mb-4 md:mb-6 shadow-[0_0_10px_rgba(193,18,31,0.3)]">
              {t('home.welcome', 'Bem-vindo ao Literafan')}
            </span>
          </RevealOnScroll>
          <RevealOnScroll animation="animate-fade-in-up" delay={0.2}>
            <p className="font-serif italic text-lg md:text-2xl text-stone-300 mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed px-2">
              {t('home.hero_sub', 'O seu ecossistema literário que te dá a chave para desbravar a imensidão de universos, que só a imaginação humana pode criar.')}
            </p>
          </RevealOnScroll>
          <RevealOnScroll animation="animate-fade-in-up" delay={0.3}>
            <h1 className="font-serif text-[2.75rem] leading-[1.1] md:text-7xl font-bold text-stone-100 mb-6 md:mb-6">
              {t('home.hero_title_1', 'Aventura Literária &')}<br />
              <span className="italic text-primary font-serif">{t('home.hero_title_2', 'Conexão Real')}</span>
            </h1>
          </RevealOnScroll>
          <RevealOnScroll animation="animate-fade-in" delay={0.6}>
            <TypingQuote />
          </RevealOnScroll>
          <RevealOnScroll animation="animate-fade-in-up" delay={0.8}>
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center px-2 md:px-4 w-full">
              <Link 
                to="/artigos" 
                className="w-full md:w-auto px-8 py-4 bg-red-950/60 backdrop-blur-md border border-red-500/70 text-white rounded-full hover:bg-red-900/80 hover:border-red-400 transition-all duration-300 shadow-[0_0_18px_rgba(255,50,50,0.55),inset_0_0_10px_rgba(255,50,50,0.25)] md:hover:shadow-[0_0_32px_rgba(255,50,50,0.9),inset_0_0_15px_rgba(255,50,50,0.4)] text-sm font-bold tracking-widest uppercase active:scale-95 md:hover:scale-105"
              >
                {t('home.explore_articles', 'Explorar Artigos')}
              </Link>
              <Link 
                to="/comunidade" 
                className="w-full md:w-auto px-8 py-4 bg-red-950/30 backdrop-blur-md border border-red-500/40 text-stone-200 rounded-full hover:bg-red-900/60 hover:border-red-400/80 transition-all duration-300 shadow-[0_0_14px_rgba(255,50,50,0.35)] md:hover:shadow-[0_0_25px_rgba(255,50,50,0.7)] text-sm font-bold tracking-widest uppercase active:scale-95 md:hover:scale-105"
              >
                {t('nav.community', 'Nossa Comunidade')}
              </Link>
            </div>
          </RevealOnScroll>
          
          <RevealOnScroll animation="animate-fade-in" delay={1.2} className="mt-24 flex justify-center">
            <div className="animate-bounce text-stone-500">
               <span className="text-xs tracking-widest uppercase mb-2 block text-center">{t('home.discover', 'Descubra')}</span>
               <div className="w-[1px] h-12 bg-gradient-to-b from-stone-500 to-transparent mx-auto"></div>
            </div>
          </RevealOnScroll>
        </div>
        </section>

        {/* Mobile Social Links Section */}
        <SocialLinksSection className="block md:hidden pb-16" />

        {/* Highlights Section */}
        <section className="py-24 relative z-10">
          <div className="container mx-auto px-6">
          <RevealOnScroll animation="animate-slide-in-left">
            <div className="flex justify-between items-end mb-16">
              <div>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-100 mb-3">{t('home.weekly_highlights', 'Destaques da Semana')}</h2>
                <div className="h-1 w-20 bg-primary"></div>
              </div>
              <Link to="/artigos" className="hidden md:flex items-center text-sm font-medium text-stone-500 hover:text-primary transition-colors group">
                {t('home.see_all_articles', 'Ver todos os artigos')} <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </RevealOnScroll>

          <div className="relative overflow-hidden">
            <div 
              className={`flex ${isTransitioning ? 'transition-transform duration-1000 ease-in-out' : ''}`}
              style={{ transform: `translateX(-${highlightSlide * 100}%)` }}
            >
              {displayArticles.length > 0 ? Array.from({ length: totalHighlightSlides + 1 }).map((_, slideIdx) => {
                const actualIdx = slideIdx % totalHighlightSlides;
                return (
                <div key={slideIdx} className="w-full flex-shrink-0 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
                  {displayArticles.slice(actualIdx * itemsPerSlide, actualIdx * itemsPerSlide + itemsPerSlide).map((article, index) => (
                    <RevealOnScroll key={`${slideIdx}-${article.id}`} delay={index * 0.2} animation="animate-fade-in-up">
                      <Link to={getArticleUrl(article)} state={{ article }} className="group cursor-pointer block w-full">
                        <div className="relative overflow-hidden aspect-[16/9] md:aspect-[4/3] mb-4 md:mb-5 rounded-3xl md:rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.6)] border border-stone-800/80 md:border-transparent md:shadow-sm bg-stone-900">
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors z-10 duration-700"></div>
                          <img 
                            src={getOptimizedImageUrl(article.imageUrl)} 
                            alt={article.imageAlt || article.title} 
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer"
                            width="460"
                            height="259"
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000 opacity-90 group-hover:opacity-100 ease-out"
                          />
                        </div>
                        <div className="pr-1 md:pr-4">
                          <div className="flex items-center text-[10px] sm:text-xs text-stone-500 mb-1.5 md:mb-2 space-x-1.5 sm:space-x-2 flex-wrap">
                            <span>{article.date}</span>
                            <span className="w-1 h-1 bg-stone-700 rounded-full"></span>
                            <span>{getReadingTime(article.content, article.excerpt)}</span>
                          </div>
                          <h3 className="font-serif text-xs min-h-[2.5rem] md:min-h-0 md:text-xl font-bold text-stone-200 mb-1 md:mb-2 group-hover:text-primary transition-colors leading-snug line-clamp-3 md:line-clamp-2">
                            <TranslatedText text={article.title} />
                          </h3>
                          <p className="hidden md:block text-stone-400 text-sm leading-relaxed line-clamp-2">
                            <TranslatedText text={article.excerpt} />
                          </p>
                        </div>
                      </Link>
                    </RevealOnScroll>
                  ))}
                </div>
              )}) : (
                <div className="w-full text-center py-12 text-stone-500 italic">
                  Nenhum artigo publicado ainda.
                </div>
              )}
            </div>
            
            {/* Slide Indicators with accessible touch target (>= 44px) */}
            <div className="flex justify-center mt-10 space-x-1">
              {Array.from({ length: totalHighlightSlides }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setIsTransitioning(true);
                    setHighlightSlide(idx);
                  }}
                  className="p-3 min-w-[44px] min-h-[44px] flex items-center justify-center -m-1 focus:outline-none"
                  aria-label={`Ir para o slide ${idx + 1}`}
                >
                  <span className={`h-2 rounded-full transition-all duration-300 block ${
                    (highlightSlide % totalHighlightSlides) === idx ? 'bg-primary w-6' : 'bg-stone-700 hover:bg-stone-500 w-2'
                  }`} />
                </button>
              ))}
            </div>
          </div>
        </div>
        </section>
      </div>

      {/* Combined Background for Quem Somos, YouTube, and Social Media */}
      <div className="relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <img 
            src="/images/bg-f2.webp" 
            alt="Library background" 
            className="w-full h-full object-cover scale-105"
            referrerPolicy="no-referrer"
            loading="lazy"
            decoding="async"
            width="1920"
            height="1080"
          />
        </div>
        {/* Gradient to blend with previous section and fade out at the bottom */}
        <div className="absolute inset-0 bg-gradient-to-b from-paper from-0% via-paper/60 via-20% to-paper to-100%"></div>

        {/* Article Carousel Section */}
        <section className="py-24 relative z-10 overflow-hidden">
          <div className="container mx-auto px-6 mb-12">
            <RevealOnScroll animation="animate-fade-in-up">
              <div className="flex justify-between items-end">
                <div>
                  <span className="text-primary font-bold tracking-widest text-xs uppercase mb-2 block">{t('home.most_read', 'Mais Lidos')}</span>
                  <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-100 mb-3">{t('home.popular_articles', 'Artigos Populares')}</h2>
                  <div className="h-1 w-20 bg-primary"></div>
                </div>
                <Link to="/artigos" className="hidden md:flex items-center text-sm font-medium text-stone-500 hover:text-primary transition-colors group">
                  {t('home.see_all', 'Ver todos')} <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </RevealOnScroll>
          </div>
          
          <div 
            className="w-full overflow-x-auto pb-8 hide-scrollbar"
            ref={articleCarouselRef}
            onMouseEnter={() => setArticleIsHovered(true)}
            onMouseLeave={() => setArticleIsHovered(false)}
            onTouchStart={() => setArticleIsHovered(true)}
            onTouchEnd={() => setArticleIsHovered(false)}
            style={{ scrollBehavior: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex px-4 md:px-3 w-max">
              {/* Infinite carousel optimized for top 8 articles with lazy-loaded thumbnails */}
              {displayArticles.length > 0 ? (
                (() => {
                  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
                  const itemsCount = Math.min(8, displayArticles.length);
                  const baseArticles = displayArticles.slice(0, itemsCount);
                  const multiplier = isMobile ? 1 : 2;
                  return Array.from({ length: itemsCount * multiplier }).map((_, index) => {
                    const i = index % itemsCount;
                    const article = baseArticles[i];
                return (
                <Link 
                  key={index} 
                  to={getArticleUrl(article)}
                  state={{ article }}
                  className="block w-[55vw] max-w-[200px] sm:max-w-none md:w-80 shrink-0 group mx-2 md:mx-3"
                >
                  <div className="relative overflow-hidden aspect-[4/3] mb-4 rounded-3xl md:rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.6)] border border-stone-800 group-hover:border-primary/50 transition-colors bg-stone-900">
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors z-10 duration-700"></div>
                    <img 
                      src={getOptimizedImageUrl(article.imageUrl)} 
                      alt={article.imageAlt || article.title} 
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer"
                      width="320"
                      height="240"
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000 opacity-90 group-hover:opacity-100 ease-out"
                    />
                  </div>
                  <div className="pr-4">
                    <div className="flex items-center text-xs text-stone-500 mb-2 space-x-2">
                      <span>{article.date}</span>
                      <span className="w-1 h-1 bg-stone-700 rounded-full"></span>
                      <span>{getReadingTime(article.content, article.excerpt)}</span>
                    </div>
                    <h3 className="font-serif text-stone-200 font-bold text-lg leading-tight mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      <TranslatedText text={article.title} />
                    </h3>
                  </div>
                </Link>
                );
                  });
                })()
              ) : (
                <div className="w-full text-center py-12 text-stone-500 italic">
                  Nenhum artigo publicado ainda.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Quem Somos Section */}
        <section className="py-32 relative z-10">
          <div className="container mx-auto px-6">
          <RevealOnScroll animation="animate-fade-in-up">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-left">
                <span className="text-primary font-bold tracking-widest text-xs uppercase mb-4 block">{t('home.our_essence', 'Nossa Essência')}</span>
                <h2 className="font-serif text-4xl md:text-5xl font-bold text-stone-100 mb-8">{t('home.who_we_are', 'Quem somos?')}</h2>
                
                <div className="space-y-4 md:space-y-6 text-stone-300 text-[15px] md:text-lg leading-relaxed font-serif">
                  <p>
                    {t('home.who_we_are_p1', 'Nós, da equipe Literafan, somos um grupo de pessoas apaixonadas pelo fantástico e maravilhoso mundo literário. Pois é nele que podemos viajar até os limites de nossa imaginação, conhecer mundos incríveis e participar de aventuras inesquecíveis no conforto de nosso lar, esquecendo brevemente dos problemas que vêm nos atormentar.')}
                  </p>
                  <p>
                    {t('home.who_we_are_p2', 'Por isso, decidimos criar uma comunidade onde todos são aceitos e bem-vindos para participar e compartilhar suas aventuras literárias, fazer novas amizades, descobrir novas paixões, construir relações com pessoas incríveis e, por fim, apresentar a todos, novos universos de histórias e aventuras fascinantes, descritos nas páginas de um simpático e querido livro.')}
                  </p>
                </div>
                
                <div className="mt-10">
                  <div className="h-1 w-24 bg-primary rounded-full opacity-50"></div>
                </div>
              </div>
              <div className="flex justify-center lg:justify-end">
                <img 
                  src={getOptimizedImageUrl("https://i.postimg.cc/x8dQw63d/Nova-logo-Edited-1.png")} 
                  alt="Literafan Logo" 
                  className="w-full max-w-md object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  width="448"
                  height="448"
                />
              </div>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* YouTube Carousel Section */}
      <section className="pt-24 pb-4 relative z-10 overflow-hidden">
        <div className="container mx-auto px-6 mb-12">
          <RevealOnScroll animation="animate-fade-in-up">
            <div className="flex justify-between items-end">
              <div>
                <span className="text-primary font-bold tracking-widest text-xs uppercase mb-2 block">{t('home.our_channel', 'Nosso Canal')}</span>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-100 mb-3">{t('home.latest_videos', 'Últimos Vídeos')}</h2>
                <div className="h-1 w-20 bg-primary"></div>
              </div>
              <a href="https://www.youtube.com/@literafan" target="_blank" rel="noopener noreferrer" className="hidden md:flex items-center text-sm font-medium text-stone-500 hover:text-primary transition-colors group">
                {t('home.access_channel', 'Acessar Canal')} <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </RevealOnScroll>
        </div>
        
        <div 
          className="w-full overflow-x-auto pb-8 hide-scrollbar"
          ref={ytCarouselRef}
          onMouseEnter={() => setYtIsHovered(true)}
          onMouseLeave={() => setYtIsHovered(false)}
          onTouchStart={() => setYtIsHovered(true)}
          onTouchEnd={() => setYtIsHovered(false)}
          style={{ scrollBehavior: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex px-4 md:px-3 w-max">
            {/* Infinite carousel optimized for 10 videos with lazy-loaded thumbnails */}
            {(() => {
              const youtubeVideos = [
                { id: "7rG6wmfIOq8", title: "A Cidade de Bronze" },
                { id: "NTW0eKcxZ88", title: "Retirantes o legado das sombras" },
                { id: "ZfYd8iCn2OU", title: "A Ilha do Tesouro" },
                { id: "B1WNfefwetI", title: "Cidade de Jade" },
                { id: "YDq1umomzcs", title: "Uma Janela Sombria" },
                { id: "DjS8mcQEE3k", title: "Babel" },
                { id: "MFVhTu8b7Ko", title: "O Circo da Noite" },
                { id: "2LNVN77N8AQ", title: "Quarta Asa" },
                { id: "8DA8jEnE4zQ", title: "O Manual do mago Frugal Para a Sobrevivência na Inglaterra Medieval" },
                { id: "TTzHEEoksuI", title: "O Cavaleiro dos Sete Reinos" }
              ];
              
              const totalItems = youtubeVideos.length;
              return Array.from({ length: totalItems * 2 }).map((_, index) => {
                const i = index % totalItems;
                const video = youtubeVideos[i];
                return (
                <a 
                  key={index} 
                  href={`https://www.youtube.com/watch?v=${video.id}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block w-[85vw] md:w-80 shrink-0 group mx-2 md:mx-3"
                  aria-label={`Assistir ao vídeo ${video.title} no YouTube`}
                >
                  <div className="relative aspect-video rounded-2xl overflow-hidden mb-4 shadow-lg border border-stone-800 group-hover:border-primary/50 transition-colors">
                    <img 
                      src={getYouTubeThumbnail(video.id, 'mq', true)} 
                      alt={`Miniatura do vídeo ${video.title}`} 
                      loading="lazy"
                      decoding="async"
                      width="320"
                      height="180"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <div className="w-12 h-12 bg-primary/90 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300 shadow-lg">
                        <svg className="w-5 h-5 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </div>
                  </div>
                  <h3 className="font-serif text-stone-200 font-bold text-lg leading-tight mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    {video.title}
                  </h3>
                  <p className="text-stone-500 text-xs">Literafan</p>
                </a>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* Destaques (Top Videos) Section */}
      <section className="pt-4 pb-24 relative z-10 overflow-hidden">
        <div className="container mx-auto px-6 mb-12">
          <RevealOnScroll animation="animate-fade-in-up">
            <div className="max-w-4xl mx-auto flex flex-col md:flex-row md:justify-between md:items-end mb-10 gap-4">
              <div>
                <span className="text-primary font-bold tracking-widest text-xs uppercase mb-2 block">{t('home.special_selection', 'Seleção Especial')}</span>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-100 mb-3">{t('home.featured_videos', 'Vídeos Destaques')}</h2>
                <div className="h-1 w-20 bg-primary"></div>
              </div>
            </div>

            <div className="relative overflow-hidden max-w-4xl mx-auto">
              <div 
                className={`flex ${isTopVideosTransitioning ? 'transition-transform duration-1000 ease-in-out' : ''}`}
                style={{ transform: `translateX(-${topVideosSlide * 100}%)` }}
              >
                {Array.from({ length: totalTopVideoSlides + 1 }).map((_, slideIdx) => {
                  const actualIdx = slideIdx % totalTopVideoSlides;
                  const slideVideos = topVideosList.slice(actualIdx * 3, actualIdx * 3 + 3);
                  if (slideVideos.length === 0) return null;
                  
                  const mainVideo = slideVideos[0];
                  const secondaryVideos = slideVideos.slice(1);
                  const startIndex = actualIdx * 3 + 1; // For TOP ranking
                  
                  return (
                    <div key={slideIdx} className="w-full flex-shrink-0 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                      {/* Featured Top Video */}
                      <a 
                        href={`https://www.youtube.com/watch?v=${mainVideo.id}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="col-span-1 md:col-span-2 relative group rounded-2xl md:rounded-[2rem] overflow-hidden aspect-[4/3] md:aspect-video shadow-2xl border border-stone-800 hover:border-primary/50 transition-colors block"
                        aria-label={`Assistir ao vídeo em destaque TOP ${startIndex}: ${mainVideo.title}`}
                      >
                        <img 
                          src={getYouTubeThumbnail(mainVideo.id, 'hq', true)} 
                          alt={`Vídeo em Destaque TOP ${startIndex}: ${mainVideo.title}`} 
                          loading="lazy"
                          decoding="async"
                          width="480"
                          height="270"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent flex flex-col justify-end p-4 md:p-8">
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 md:w-16 md:h-16 bg-primary/90 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100 transition-all duration-300 shadow-[0_0_30px_rgba(236,72,153,0.5)]">
                            <svg className="w-5 h-5 md:w-8 md:h-8 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                          </div>
                          <div>
                            <span className="inline-block px-2 py-0.5 md:px-3 md:py-1 bg-primary/20 text-primary border border-primary/30 rounded-full text-[10px] md:text-xs font-bold mb-1.5 md:mb-3 backdrop-blur-sm">TOP {startIndex}</span>
                            <h3 className="font-serif text-stone-100 font-bold text-lg md:text-3xl leading-tight mb-1 md:mb-2 group-hover:text-primary transition-colors line-clamp-2">
                               {mainVideo.title}
                            </h3>
                            {mainVideo.description && (
                              <p className="text-stone-400 text-[10px] md:text-sm md:opacity-0 md:group-hover:opacity-100 md:max-h-0 md:group-hover:max-h-20 transition-all duration-500 overflow-hidden delay-100 line-clamp-2 md:line-clamp-none">
                                {mainVideo.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </a>

                      {/* Smaller Top Videos */}
                      {secondaryVideos.length > 0 && (
                        <div className="flex flex-row md:flex-col gap-4 md:gap-6">
                          {secondaryVideos.map((video, idx) => (
                            <a 
                              key={video.id}
                              href={`https://www.youtube.com/watch?v=${video.id}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="relative group rounded-xl md:rounded-2xl overflow-hidden shadow-xl border border-stone-800 hover:border-primary/50 transition-colors flex-1 block aspect-video md:aspect-auto md:h-full w-1/2 md:w-full"
                              aria-label={`Assistir ao vídeo TOP ${startIndex + idx + 1}: ${video.title}`}
                            >
                              <img 
                                src={getYouTubeThumbnail(video.id, 'mq', true)} 
                                alt={`Top Vídeo ${startIndex + idx + 1}: ${video.title}`} 
                                loading="lazy"
                                decoding="async"
                                width="320"
                                height="180"
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-black/20 flex flex-col justify-end p-2 md:p-5">
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-8 h-8 md:w-12 md:h-12 bg-primary/90 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300 shadow-lg">
                                    <svg className="w-4 h-4 md:w-5 md:h-5 ml-0.5 md:ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                  </div>
                                </div>
                                <div className="relative z-10">
                                  <span className="inline-block px-1.5 py-0 md:px-2 md:py-0.5 bg-stone-800/80 text-stone-300 border border-stone-600 rounded text-[8px] md:text-[10px] font-bold mb-1 md:mb-2 backdrop-blur-sm">TOP {startIndex + idx + 1}</span>
                                  <h3 className="font-serif text-stone-200 font-bold text-xs md:text-lg leading-tight group-hover:text-primary transition-colors line-clamp-2">
                                    {video.title}
                                  </h3>
                                </div>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Slide Indicators with accessible touch area (>= 44px) */}
              <div className="flex justify-center mt-10 space-x-1">
                {Array.from({ length: totalTopVideoSlides }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setIsTopVideosTransitioning(true);
                      setTopVideosSlide(idx);
                    }}
                    className="p-3 min-w-[44px] min-h-[44px] flex items-center justify-center -m-1 focus:outline-none"
                    aria-label={`Ir para slide de destaques ${idx + 1}`}
                  >
                    <span className={`h-2 rounded-full transition-all duration-300 block ${
                      (topVideosSlide % totalTopVideoSlides) === idx 
                        ? 'w-6 bg-primary' 
                        : 'bg-stone-700 hover:bg-stone-500 w-2'
                    }`} />
                  </button>
                ))}
              </div>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* Social Media Section */}
      <SocialLinksSection className="hidden md:block" />

      {/* Community Teaser */}
      <section className="py-24 relative z-10 overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <RevealOnScroll animation="animate-slide-in-left" className="relative order-2 md:order-1">
              <div className="absolute -top-10 -left-10 w-64 h-64 bg-primary rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse-slow"></div>
              <div className="relative grid grid-cols-2 gap-4">
                <div className="space-y-4 mt-8">
                  <div className="bg-stone-900 p-6 rounded-sm shadow-lg border border-stone-800 transform hover:-translate-y-2 transition-transform duration-500 animate-float" style={{ animationDelay: '0s' }}>
                    <p className="font-serif italic text-stone-400 text-sm mb-4">"Acabei de terminar 1984 e estou sem palavras. Alguém para discutir o final?"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-stone-800 flex items-center justify-center text-xs font-bold text-stone-300 border border-stone-700">
                        AS
                      </div>
                      <span className="text-xs font-bold text-stone-300">Ana Silva</span>
                    </div>
                  </div>
                  <div className="bg-stone-900 p-6 rounded-sm shadow-lg border border-stone-800 transform hover:-translate-y-2 transition-transform duration-500 animate-float" style={{ animationDelay: '2s' }}>
                    <p className="font-serif italic text-stone-400 text-sm mb-4">"Meu poema favorito de Fernando Pessoa sempre será Tabacaria."</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-stone-800 flex items-center justify-center text-xs font-bold text-stone-300 border border-stone-700">
                        JP
                      </div>
                      <span className="text-xs font-bold text-stone-300">João P.</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-primary text-white p-6 rounded-sm shadow-lg transform hover:-translate-y-2 transition-transform duration-500 mt-4 animate-float" style={{ animationDelay: '1s' }}>
                    <PenTool size={24} className="mb-4 text-white/80" />
                    <h3 className="font-bold text-lg mb-2">Escreva & Compartilhe</h3>
                    <p className="text-xs text-white/80">Publique suas resenhas, contos e poesias para milhares de leitores.</p>
                  </div>
                   <div className="bg-stone-900 p-6 rounded-sm shadow-lg border border-stone-800 transform hover:-translate-y-2 transition-transform duration-500 animate-float" style={{ animationDelay: '3s' }}>
                    <p className="font-serif italic text-stone-400 text-sm mb-4">"Busco recomendações de Fantasia com boa construção de mundo."</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-stone-800 flex items-center justify-center text-xs font-bold text-stone-300 border border-stone-700">
                        ML
                      </div>
                      <span className="text-xs font-bold text-stone-300">Mariana L.</span>
                    </div>
                  </div>
                </div>
              </div>
            </RevealOnScroll>
            
            <div className="order-1 md:order-2">
              <RevealOnScroll animation="animate-slide-in-right" delay={0.2}>
                <span className="text-primary font-bold tracking-widest text-xs uppercase mb-2 block">Comunidade</span>
                <h2 className="font-serif text-4xl md:text-5xl font-bold text-stone-100 mb-6">Não leia sozinho.</h2>
                <p className="text-stone-400 text-lg leading-relaxed mb-8">
                  Junte-se a uma comunidade vibrante de leitores, escritores e entusiastas. 
                  Compartilhe suas leituras, descubra novos mundos e faça amizades através das páginas.
                </p>
              </RevealOnScroll>
              <RevealOnScroll animation="animate-slide-in-right" delay={0.4}>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-stone-800 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                      <Users size={14} />
                    </div>
                    <span className="text-stone-300 font-medium">Debates semanais sobre clássicos</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-stone-800 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                      <PenTool size={14} />
                    </div>
                    <span className="text-stone-300 font-medium">Espaço para autores independentes</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-stone-800 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                      <BookOpen size={14} />
                    </div>
                    <span className="text-stone-300 font-medium">Clube do livro virtual</span>
                  </li>
                </ul>
              </RevealOnScroll>
              <RevealOnScroll animation="animate-fade-in-up" delay={0.8}>
                <Link to="/comunidade" className="inline-flex items-center px-8 py-4 bg-red-950/60 backdrop-blur-md border border-red-500/70 text-white font-bold uppercase tracking-wider text-sm rounded-full hover:bg-red-900/80 hover:border-red-400 transition-all shadow-[0_0_18px_rgba(255,50,50,0.55),inset_0_0_10px_rgba(255,50,50,0.25)] hover:shadow-[0_0_32px_rgba(255,50,50,0.9),inset_0_0_15px_rgba(255,50,50,0.4)] hover:scale-105 transform duration-300">
                  Acessar Comunidade <ArrowRight size={16} className="ml-2" />
                </Link>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>
      
      {/* Dynamic Sections */}
      {sections.filter(s => s.location === 'home-bottom').map(section => (
        <section key={section.id} className="py-12 relative z-10">
          <div 
            className="container mx-auto px-6"
            dangerouslySetInnerHTML={{ __html: section.contentHtml }}
          />
        </section>
      ))}

      {/* Newsletter Call to Action */}
      <section className="py-16 sm:py-24 text-white relative z-10 overflow-hidden px-4 sm:px-6">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="bg-stone-900/90 border border-stone-800 rounded-3xl p-6 sm:p-12 shadow-2xl backdrop-blur-xl text-center">
            <RevealOnScroll animation="animate-zoom-in">
              <BookOpen size={42} className="mx-auto text-primary mb-4 sm:mb-6" />
              <h2 className="font-serif text-2xl sm:text-3xl md:text-5xl font-bold mb-3 sm:mb-4">A Literatura em sua Caixa de Entrada</h2>
              <p className="text-stone-400 max-w-xl mx-auto mb-8 sm:mb-10 text-sm sm:text-lg">
                Curadoria semanal de artigos, lançamentos e discussões profundas. Sem spam, apenas boa leitura.
              </p>
              <div className="flex flex-col sm:flex-row justify-center max-w-md mx-auto gap-3">
                <input 
                  type="email" 
                  placeholder="Seu melhor e-mail" 
                  value={newsletterEmail}
                  onChange={(e) => { setNewsletterEmail(e.target.value); setNewsletterStatus('idle'); }}
                  className={`w-full px-5 py-3.5 sm:px-6 sm:py-4 bg-stone-800/80 backdrop-blur-sm border rounded-2xl sm:rounded-full text-white placeholder-stone-500 focus:outline-none focus:border-red-500 focus:bg-stone-800 transition-all text-sm ${newsletterStatus === 'error' ? 'border-red-500' : 'border-stone-700'}`}
                  disabled={newsletterStatus === 'loading' || newsletterStatus === 'success'}
                />
                <button 
                  onClick={handleSubscribe}
                  disabled={newsletterStatus === 'loading' || newsletterStatus === 'success'}
                  className={`w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 ${newsletterStatus === 'success' ? 'bg-green-600 border-green-500 hover:bg-green-500' : 'bg-red-950/70 hover:bg-red-900/90 border-red-500/80 shadow-[0_0_18px_rgba(255,50,50,0.55),inset_0_0_10px_rgba(255,50,50,0.25)] hover:shadow-[0_0_30px_rgba(255,50,50,0.9),inset_0_0_15px_rgba(255,50,50,0.4)]'} backdrop-blur-md border text-white font-bold uppercase tracking-wider text-xs sm:text-sm rounded-2xl sm:rounded-full transition-all flex-shrink-0 active:scale-95 sm:hover:scale-105 transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {newsletterStatus === 'loading' ? 'Enviando...' : newsletterStatus === 'success' ? 'Inscrito!' : 'Inscrever'}
                </button>
              </div>
              {newsletterStatus === 'error' && (
                <p className="text-red-500 text-xs sm:text-sm mt-3">Por favor, insira um e-mail válido ou tente novamente.</p>
              )}
              {newsletterStatus === 'success' && (
                <p className="text-green-400 text-xs sm:text-sm mt-3">Obrigado por se inscrever!</p>
              )}
            </RevealOnScroll>
          </div>
        </div>
      </section>
      
      {/* Footer rendered inside the background wrapper */}
      <Footer />
      </div>
    </div>
  );
};

export default Home;